import React, { useState } from "react";
import { Box } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { LEADERBOARDS } from "../../_config/LeaderboardConfig";
import LeaderboardSideMenu from "../../components/dashboard/ranking/LeaderboardSideMenu";
import MemberAcademicYearSelect from "../../components/member/MemberAcademicYearSelect";
import {
    ALL_ACADEMIC_YEARS,
    formatAcademicYearRangeLabel,
} from "../../helper/AcademicYearHelper";

const RIGHT_PANEL_WIDTH = 240;
const YEAR_OPTIONS = [ALL_ACADEMIC_YEARS, 2026, 2025];

const SPONSOR_LOGOS = [
    "/assets/logos/bright-future-logo-color.png",
];

/**
 * Parse leaderboard_code (e.g. "most_active_member-secondary") into { type, orgType }.
 * Strategy: the orgType is always the last dash-separated segment ("primary" or "secondary");
 * everything before it (joined back with "-") is the type.
 */
function parseLeaderboardCode(code = "") {
    const parts = code.split("-");
    const orgType = parts[parts.length - 1]; // "primary" | "secondary"
    const type = parts.slice(0, parts.length - 1).join("-"); // e.g. "most_active_member"
    return { type, orgType };
}

function getInitialIndex(leaderboardCode) {
    const { type, orgType } = parseLeaderboardCode(leaderboardCode);
    const idx = LEADERBOARDS.findIndex((lb) => lb.type === type && lb.orgType === orgType);
    return idx >= 0 ? idx : 0;
}

function getInitialYear(locationYear) {
    if (locationYear != null && YEAR_OPTIONS.includes(Number(locationYear))) {
        return Number(locationYear);
    }
    return ALL_ACADEMIC_YEARS;
}

const TOP_RANK_COUNT = 8; // Number of top ranks to show in full-screen mode

export default function FullScreenLeaderboardPage() {
    const { leaderboard_code } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [currentIndex, setCurrentIndex] = useState(() => getInitialIndex(leaderboard_code));
    const [selectedYear, setSelectedYear] = useState(() => getInitialYear(location.state?.year));

    const handleSelect = (index) => {
        setCurrentIndex(index);
        const { type, orgType } = LEADERBOARDS[index];
        navigate(`/leaderboards/${type}-${orgType}`, {
            replace: true,
            state: { year: selectedYear },
        });
    };

    const handleYearChange = (year) => {
        setSelectedYear(year);
        const { type, orgType } = LEADERBOARDS[currentIndex];
        navigate(`/leaderboards/${type}-${orgType}`, {
            replace: true,
            state: { year },
        });
    };

    const CurrentLeaderboard = LEADERBOARDS[currentIndex].component;
    const currentOrgType = LEADERBOARDS[currentIndex].orgType;

    return (
        <Box
            sx={{
                display: "flex",
                height: "100vh",
                width: "100vw",
                background: "linear-gradient(135deg, #0A051E 0%, #0D0620 50%, #0A0A1A 100%)",
                overflow: "hidden",
            }}
        >
            {/* ── Center Panel ── */}
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", position: "relative" }}>
                <CurrentLeaderboard
                    orgType={currentOrgType}
                    year={selectedYear}
                    refreshInterval={-1}
                    topRankCount={TOP_RANK_COUNT}
                />
                <Box
                    sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        zIndex: 1,
                    }}
                >
                    <MemberAcademicYearSelect
                        value={selectedYear}
                        onChange={handleYearChange}
                        years={YEAR_OPTIONS}
                        formatLabel={formatAcademicYearRangeLabel}
                        sx={{ minWidth: 120 }}
                    />
                </Box>
            </Box>

            {/* ── Right Panel ── */}
            <Box
                sx={{
                    width: RIGHT_PANEL_WIDTH,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    borderLeft: "1px solid rgba(130, 60, 255, 0.4)",
                    background: "rgba(10, 5, 30, 0.95)",
                }}
            >
                <LeaderboardSideMenu
                    leaderboards={LEADERBOARDS}
                    currentIndex={currentIndex}
                    onSelect={handleSelect}
                    onBack={() => navigate("/dashboard")}
                />

                {/* Sponsor Logos — pinned to bottom */}
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderTop: "1px solid rgba(130, 60, 255, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        alignItems: "center",
                    }}
                >
                    {SPONSOR_LOGOS.map((logo, i) => (
                        <Box
                            key={i}
                            component="img"
                            src={logo}
                            alt={`Sponsor ${i + 1}`}
                            sx={{
                                maxWidth: "100%",
                                maxHeight: 48,
                                objectFit: "contain",
                                opacity: 0.85,
                                filter: "brightness(0.95)",
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
